import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';

interface Project {
  title: string;
  description: string;
  technologies: string[];
  github?: string;
  demo?: string;
}

const projects: Project[] = [
  {
    title: 'projects.recommendation.title',
    description: 'projects.recommendation.description',
    technologies: ['Django', 'Python', 'TensorFlow', 'Scikit-learn', 'Pandas', 'NumPy'],
    github: '#',
  },
  {
    title: 'projects.moncomptable.title',
    description: 'projects.moncomptable.description',
    technologies: ['React Native', 'ContextAPI', 'Expo', 'Firebase'],
    github: '#',
    demo: '#',
  },
  {
    title: 'projects.stadioapp.title',
    description: 'projects.stadioapp.description',
    technologies: ['React Native', 'Expo', 'Axios', 'SpringBoot', 'MySQL'],
    github: '#',
  },
  {
    title: 'projects.university.title',
    description: 'projects.university.description',
    technologies: ['React', 'SpringBoot', 'Eureka', 'OpenFeign', 'MySQL'],
    github: '#',
  },
  {
    title: 'projects.language.title',
    description: 'projects.language.description',
    technologies: ['HTML', 'CSS', 'JavaScript', 'Bootstrap', 'JEE', 'MySQL'],
    github: '#',
  },
  {
    title: 'projects.holiday.title',
    description: 'projects.holiday.description',
    technologies: ['HTML', 'CSS', 'PHP', 'MySQL'],
    github: '#',
  }
];

const ProjectCard: React.FC<{ project: Project; index: number }> = ({ project, index }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { t } = useLanguage();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="card group"
    >
      <h3 className="text-xl font-bold text-secondary mb-3 break-words">{t(project.title)}</h3>
      <p className="text-base-content/60 mb-4 break-words">{t(project.description)}</p>
      
      <div className="flex flex-wrap gap-2 mb-4">
        {project.technologies.map((tech, i) => (
          <span
            key={i}
            className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm whitespace-nowrap"
          >
            {tech}
          </span>
        ))}
      </div>

      <div className="flex gap-4">
        {project.github && (
          <motion.a
            href={project.github}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="text-base-content/60 hover:text-primary transition-colors"
          >
            <FaGithub size={20} />
          </motion.a>
        )}
        {project.demo && (
          <motion.a
            href={project.demo}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="text-base-content/60 hover:text-primary transition-colors"
          >
            <FaExternalLinkAlt size={20} />
          </motion.a>
        )}
      </div>
    </motion.div>
  );
};

const Projects: React.FC = () => {
  const { t } = useLanguage();
  
  return (
    <section id="projects" className="section-padding">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="heading-secondary mb-4 text-primary">{t('projects.title')}</h2>
          <div className="w-24 h-1 bg-secondary mx-auto rounded-full" />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {projects.map((project, index) => (
            <ProjectCard key={index} project={project} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects; 